<script setup>
import { ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  tour:      { type: Object, required: true },
  base_path: { type: String, default: '/tours' }
})

const activeIdx = ref(0)
const photos = props.tour.photo_urls?.length ? props.tour.photo_urls : []
</script>

<template>
  <Head>
    <title>{{ tour.title }} · {{ t('public.head_title') }}</title>
    <meta
      name="description"
      :content="tour.summary || tour.description"
    >
  </Head>

  <section class="show">
    <a
      :href="base_path"
      class="back"
    >← {{ t('public.tours.back') }}</a>
    <h1 class="show__title">
      {{ tour.title }}
    </h1>
    <p
      v-if="tour.summary"
      class="show__summary"
    >
      {{ tour.summary }}
    </p>

    <div class="show__layout">
      <div class="gallery">
        <div class="gallery__main">
          <img
            v-if="photos[activeIdx]"
            :src="photos[activeIdx]"
            :alt="tour.title"
          >
          <div
            v-else
            class="gallery__placeholder"
          >
            {{ tour.title?.[0] }}
          </div>
        </div>
        <ul
          v-if="photos.length > 1"
          class="thumbs"
        >
          <li
            v-for="(p, i) in photos"
            :key="p"
          >
            <button
              :class="['thumb', i === activeIdx && 'is-active']"
              @click="activeIdx = i"
            >
              <img
                :src="p"
                alt=""
              >
            </button>
          </li>
        </ul>
      </div>

      <div class="info">
        <section class="card">
          <h2 class="section-title">
            {{ t('public.tours.sections.about') }}
          </h2>
          <p class="prose">
            {{ tour.description }}
          </p>
        </section>

        <section
          v-if="tour.inclusions"
          class="card"
        >
          <h2 class="section-title">
            {{ t('public.tours.sections.included') }}
          </h2>
          <p class="prose">
            {{ tour.inclusions }}
          </p>
        </section>

        <section class="card">
          <h2 class="section-title">
            {{ t('public.tours.sections.details') }}
          </h2>
          <dl class="dl">
            <div><dt>{{ t('public.tours.fields.duration') }}</dt><dd>{{ Math.round(tour.duration_minutes / 60) }}h</dd></div>
            <div>
              <dt>{{ t('public.tours.fields.price_adult') }}</dt><dd class="dl__price">
                {{ tour.price_adult }}
              </dd>
            </div>
            <div v-if="tour.price_child">
              <dt>{{ t('public.tours.fields.price_child') }}</dt><dd>{{ tour.price_child }}</dd>
            </div>
            <div v-if="tour.max_group_size">
              <dt>{{ t('public.tours.fields.max_group_size') }}</dt><dd>{{ tour.max_group_size }}</dd>
            </div>
            <div v-if="tour.languages_offered">
              <dt>{{ t('public.tours.fields.languages_offered') }}</dt><dd>{{ tour.languages_offered }}</dd>
            </div>
            <div v-if="tour.meeting_point_address">
              <dt>{{ t('public.tours.fields.meeting_point') }}</dt><dd>{{ tour.meeting_point_address }}</dd>
            </div>
            <div v-if="tour.meeting_point_description">
              <dt>{{ t('public.tours.fields.meeting_point_description') }}</dt>
              <dd class="prose-inline">{{ tour.meeting_point_description }}</dd>
            </div>
          </dl>
        </section>

        <section
          v-if="tour.contact_intents && Object.keys(tour.contact_intents).length"
          class="card cta"
        >
          <h2 class="section-title">
            {{ t('public.tours.cta_title') }}
          </h2>
          <div class="cta__buttons">
            <a
              v-for="(url, platform) in tour.contact_intents"
              :key="platform"
              :href="url"
              target="_blank"
              rel="noopener"
              class="cta__btn"
            >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<style scoped>
.show { max-width: 60rem; margin: 0 auto; padding: 2rem 1.5rem 4rem; }
.back { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.show__title { font-family: var(--font-display, var(--display)); font-size: clamp(1.75rem, 4vw, 2.25rem); margin: 0.5rem 0 0.5rem; color: var(--ink); font-weight: 700; }
.show__summary { color: var(--inkSoft); margin: 0 0 1.5rem; font-size: 1.0625rem; line-height: 1.6; }
.show__layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .show__layout { grid-template-columns: 1.1fr 1fr; } }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 0.75rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.gallery__placeholder { font-size: 4rem; font-weight: 700; color: var(--muted); }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 4.5rem; height: 3.5rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1.25rem; }
.section-title { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.prose-inline { white-space: pre-wrap; }
.dl { display: grid; gap: 0.5rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.8125rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 0.9375rem; }
.dl__price { color: var(--accent); font-weight: 600; }
.cta__buttons { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.cta__btn { padding: 0.5rem 0.95rem; background: var(--accent); color: var(--accentInk); border-radius: var(--radius-button, 8px); text-decoration: none; font-size: 0.875rem; font-weight: 600; }
</style>
